import { Button } from '@heroui/react';
import classNames from 'classnames';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import DynamicT from '@/shared/components/DynamicT';
import MatrixLoader from '@/shared/components/MatrixLoader';

import { optionButtonClass, optionButtonProps } from './option-button-class';
import { type SocialBrand, socialBrandLogoClass } from './social-brand';

export type Props = {
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly logo: string;
  readonly name: Record<string, string>;
  /** Set only for Apple and Google; resolved by the caller via `getSocialBrand`. */
  readonly brand?: SocialBrand;
  /** A recall hint only — it neither reorders nor pre-selects. */
  readonly isLastUsed?: boolean;
  readonly onClick?: () => void;
};

/**
 * One provider in the compact social grid: logo only, no visible label. The provider name
 * survives as the accessible name (`aria-label`) with the logo `alt=""`, so it is announced
 * exactly once.
 */
const SocialTileButton = ({
  isDisabled,
  isLoading = false,
  className,
  name,
  logo,
  brand,
  isLastUsed = false,
  onClick,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const localName = name[language] ?? name.en;
  const lastUsedDescriptionId = useId();
  const isLoadingActive = useDebouncedLoader(isLoading, 300);

  return (
    <Button
      {...optionButtonProps}
      className={classNames(
        optionButtonClass,
        // A ~100px tile needs HeroUI's firmer press; the rows' 0.985 moves it under a pixel.
        'px-2 active:[transform:scale(0.96)] data-[pressed=true]:[transform:scale(0.96)]',
        className
      )}
      aria-label={t('action.sign_in_with', { name: localName })}
      aria-describedby={isLastUsed ? lastUsedDescriptionId : undefined}
      isDisabled={isDisabled}
      isPending={isLoadingActive}
      // RAC drops `title`; it is the only hover cue for sighted pointer users once the label is gone.
      render={(props) => <button {...props} title={localName} />}
      onPress={onClick}
    >
      {logo && (
        <img
          src={logo}
          alt=""
          className={classNames(
            'size-6 shrink-0 object-contain transition-opacity',
            brand && socialBrandLogoClass[brand],
            isLoadingActive && 'opacity-30'
          )}
        />
      )}
      {isLoadingActive && (
        <span className="absolute inset-0 flex items-center justify-center">
          <MatrixLoader size="sm" />
        </span>
      )}
      {isLastUsed && (
        <>
          <span aria-hidden className="absolute inset-e-3 top-2 size-1.5 rounded-full bg-accent" />
          <span hidden id={lastUsedDescriptionId}>
            <DynamicT forKey="description.last_used" />
          </span>
        </>
      )}
    </Button>
  );
};

export default SocialTileButton;
